import { Button, Center, Divider, Group, Space, Stack, Text, ThemeIcon } from "@mantine/core"
import { useTranslation } from "react-i18next"
import { useAPIResource, useUsers } from "@/hooks"
import { checkSurvey, type TSession } from "@/types"
import { activitiesAPI, setQueryCache } from "@/utils"
import { AdminButton } from "../controls"
import { UserLine } from "../people"
import { SurveyInfo } from "../surveys/SurveyInfo"
import { AdminLink, AppLoader, DateTime, ImageWithPlaceholder, icons, Modal404, SuperOnly } from "../utils"
import { ActivityCategories } from "./ActivityCategories"
import { ActivityImagePlaceholder } from "./ActivityImagePlaceholder"
import { ScheduleActivityWrapper } from "./schedule"

export function ActivityInfo({ activityId, session }: { activityId: string; session?: TSession }) {
  const { t } = useTranslation()
  const { findUser } = useUsers()
  const { data: activity, isPending } = useAPIResource(activitiesAPI, {
    id: activityId,
    ...setQueryCache(3600e3),
  })
  const isSurvey = checkSurvey(activity)

  if (isPending) return <AppLoader />
  if (!activity) return <Modal404 />

  const owner = findUser(activity.user_id!)

  return (
    <Stack gap="xs">
      <Group>
        <DateTime timestamp={activity.updated} />
        {owner?.id ? (
          <UserLine compact user={owner} isActive={owner.is_active} ml="auto" />
        ) : (
          <Group ml="auto" gap="xs" px="xs">
            <ThemeIcon size={26} radius={13} color="blue">
              <icons.Folder size={18} />
            </ThemeIcon>
            <Text fw={500}>{t("Basic template")}</Text>
          </Group>
        )}
      </Group>
      <ImageWithPlaceholder
        mah={160}
        radius="lg"
        alt={activity.title}
        src={activity.picture}
        placeholder={<ActivityImagePlaceholder activity={activity} w="100%" h={160} radius="lg" />}
      />
      <ActivityCategories activity={activity} />
      <Text>{activity.subtitle}</Text>
      {isSurvey && <SurveyInfo activity={activity} session={session} />}
      <Space />
      <Center mt="auto">
        <ScheduleActivityWrapper title={activity.title} activityId={activity.id} activityType={activity.type}>
          <Button leftSection={<icons.Add />}>{t("Schedule")}</Button>
        </ScheduleActivityWrapper>
      </Center>
      <SuperOnly>
        <Divider mt="md" />
        <Group justify="right">
          <AdminButton>
            <AdminLink resource="activity" id={activity.id}>
              Edit Activity
            </AdminLink>
          </AdminButton>
          {isSurvey && (
            <AdminButton>
              <AdminLink sort="order" resource="surveyquestion" filter={{ activity_id: activity.id }}>
                Edit Survey Questions
              </AdminLink>
            </AdminButton>
          )}
        </Group>
      </SuperOnly>
    </Stack>
  )
}
